import React, { useState } from 'react';
import { Mail, MapPin, Send, CheckCircle2, Upload, X, Image as ImageIcon } from 'lucide-react';
import { contactInfo } from '../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactFormWithFiles = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectDetails: '',
    squareFootage: ''
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [wallPhoto, setWallPhoto] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [wallPreview, setWallPreview] = useState(null);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleWallPhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setWallPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setWallPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogoFile = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const removeWallPhoto = () => {
    setWallPhoto(null);
    setWallPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('projectDetails', formData.projectDetails);
      formDataToSend.append('squareFootage', formData.squareFootage);
      
      if (logoFile) {
        formDataToSend.append('logoFile', logoFile);
      }
      if (wallPhoto) {
        formDataToSend.append('wallPhoto', wallPhoto);
      }

      await axios.post(`${API}/contact-submissions-with-files`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        projectDetails: '',
        squareFootage: ''
      });
      setLogoFile(null);
      setWallPhoto(null);
      setLogoPreview(null);
      setWallPreview(null);
      
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <pattern id="contact-serpent" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M10,40 Q20,20 30,40 T50,40 T70,40" stroke="#8A2BE2" strokeWidth="1.5" fill="none" />
            </pattern>
            <rect width="100%" height="100%" fill="url(#contact-serpent)" />
          </svg>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Get a <span className="text-purple-400">Quote</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Upload your wall photo and logo, and we'll create a custom mural design for you
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent mx-auto mt-6"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Contact Info */}
          <div className="space-y-4">
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
              <h3 className="text-xl font-bold text-white mb-4">Contact Information</h3>
              
              <div className="space-y-4">
                {/* Email */}
                <a 
                  href={`mailto:${contactInfo.emailAlt}`}
                  className="flex items-center gap-3 p-3 bg-purple-600/10 rounded-lg border border-purple-500/20 hover:border-purple-500 hover:bg-purple-600/20 transition-all duration-300 group"
                  data-testid="contact-email-link"
                >
                  <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Email Us</p>
                    <p className="text-sm font-bold text-white group-hover:text-purple-400 transition-colors break-all">
                      {contactInfo.emailAlt}
                    </p>
                  </div>
                </a>

                {/* Location */}
                <div className="flex items-center gap-3 p-3 bg-purple-600/10 rounded-lg border border-purple-500/20">
                  <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Location</p>
                    <p className="text-sm font-bold text-white">{contactInfo.location}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form (spans 2 columns) */}
          <div className="lg:col-span-2 bg-black border-2 border-purple-500 rounded-2xl p-8 shadow-2xl shadow-purple-500/20">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/50">
                <Send className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Get Your Free Quote</h3>
              <p className="text-gray-400">
                Upload your photos and tell us about your project
              </p>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-500/20 border border-green-500 rounded-lg flex items-center gap-3" data-testid="success-message">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                <p className="text-green-500 font-bold">Quote request sent! We'll contact you within 24 hours.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg" data-testid="error-message">
                <p className="text-red-500 font-bold">Something went wrong. Please try again or email us directly.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
              {/* File Upload Section */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {/* Logo Upload */}
                <div>
                  <label className="block text-gray-300 font-bold mb-2 text-sm">Your Logo/Graphic</label>
                  {!logoPreview ? (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-purple-500 rounded-lg cursor-pointer hover:bg-purple-600/10 transition-all" data-testid="logo-upload-area">
                      <Upload className="w-8 h-8 text-purple-400 mb-2" />
                      <p className="text-sm text-gray-400">Click to upload logo</p>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB</p>
                      <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} data-testid="logo-file-input" />
                    </label>
                  ) : (
                    <div className="relative h-32">
                      <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain bg-gray-900 rounded-lg border border-purple-500" />
                      <button
                        type="button"
                        onClick={removeLogoFile}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                        data-testid="remove-logo-btn"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Wall Photo Upload */}
                <div>
                  <label className="block text-gray-300 font-bold mb-2 text-sm">Photo of Your Wall</label>
                  {!wallPreview ? (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-purple-500 rounded-lg cursor-pointer hover:bg-purple-600/10 transition-all" data-testid="wall-upload-area">
                      <ImageIcon className="w-8 h-8 text-purple-400 mb-2" />
                      <p className="text-sm text-gray-400">Click to upload wall photo</p>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 10MB</p>
                      <input type="file" className="hidden" accept="image/*" onChange={handleWallPhotoUpload} data-testid="wall-file-input" />
                    </label>
                  ) : (
                    <div className="relative h-32">
                      <img src={wallPreview} alt="Wall preview" className="w-full h-full object-cover bg-gray-900 rounded-lg border border-purple-500" />
                      <button
                        type="button"
                        onClick={removeWallPhoto}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                        data-testid="remove-wall-btn"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-gray-300 font-bold mb-2 text-sm">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                    placeholder="Your name"
                    data-testid="name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-gray-300 font-bold mb-2 text-sm">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                    placeholder="your@email.com"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="block text-gray-300 font-bold mb-2 text-sm">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                    placeholder="(806) 555-1234"
                    data-testid="phone-input"
                  />
                </div>

                <div>
                  <label htmlFor="squareFootage" className="block text-gray-300 font-bold mb-2 text-sm">Square Footage</label>
                  <input
                    type="text"
                    id="squareFootage"
                    name="squareFootage"
                    value={formData.squareFootage}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all"
                    placeholder="e.g., 100 sq ft"
                    data-testid="sqft-input"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="projectDetails" className="block text-gray-300 font-bold mb-2 text-sm">Project Details *</label>
                <textarea
                  id="projectDetails"
                  name="projectDetails"
                  value={formData.projectDetails}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all resize-none"
                  placeholder="Describe your project: wall type, location, design ideas..."
                  data-testid="details-textarea"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-purple-600 text-white rounded-full font-bold text-lg hover:bg-purple-700 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                data-testid="submit-btn"
              >
                {isSubmitting ? 'Sending...' : 'Send Quote Request'}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-800 text-center">
              <p className="text-gray-400 text-sm">
                Or email us at <a href={`mailto:${contactInfo.emailAlt}`} className="text-purple-400 hover:text-purple-300 font-bold">{contactInfo.emailAlt}</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormWithFiles;
